import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

export interface Notification {
  id: string;
  tenant_id: string;
  user_id: string;
  type: "info" | "warning" | "success" | "error" | "task" | "activity";
  title: string;
  message: string | null;
  link: string | null;
  entity_type: string | null;
  entity_id: string | null;
  read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationPreferences {
  id: string;
  tenant_id: string;
  user_id: string;
  notify_task_due: boolean;
  notify_task_assigned: boolean;
  notify_deal_stage_change: boolean;
  notify_new_lead: boolean;
  notify_activity_reminder: boolean;
  notify_contract_signed: boolean;
  notify_proposal_viewed: boolean;
  email_notifications: boolean;
  created_at: string;
  updated_at: string;
}

export function useNotifications() {
  const { user, currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Fetch notifications
  const { data: notifications = [], isLoading, refetch } = useQuery({
    queryKey: ["notifications", tenantId, user?.id],
    queryFn: async () => {
      if (!tenantId || !user?.id) return [];
      
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("tenant_id", tenantId)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!tenantId && !!user?.id,
  });

  // Unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Mark as read
  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Mark all as read
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      if (!tenantId || !user?.id) return;
      
      const { error } = await supabase
        .from("notifications")
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("tenant_id", tenantId)
        .eq("user_id", user.id)
        .eq("read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Delete notification
  const deleteNotification = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Clear all notifications
  const clearAll = useMutation({
    mutationFn: async () => {
      if (!tenantId || !user?.id) return;
      
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("tenant_id", tenantId)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Subscribe to realtime updates
  useEffect(() => {
    if (!tenantId || !user?.id) return;

    const channel = supabase
      .channel("notifications-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId, user?.id, refetch]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
    deleteNotification: deleteNotification.mutate,
    clearAll: clearAll.mutate,
    refetch,
  };
}

export function useNotificationPreferences() {
  const { user, currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const { data: preferences, isLoading } = useQuery({
    queryKey: ["notification-preferences", tenantId, user?.id],
    queryFn: async () => {
      if (!tenantId || !user?.id) return null;
      
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("tenant_id", tenantId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data as NotificationPreferences | null;
    },
    enabled: !!tenantId && !!user?.id,
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!tenantId || !user?.id) throw new Error("Não autenticado");

      const { data: existing } = await supabase
        .from("notification_preferences")
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("notification_preferences")
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq("id", existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("notification_preferences")
          .insert({
            tenant_id: tenantId,
            user_id: user.id,
            ...updates,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
    },
  });

  return {
    preferences,
    isLoading,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
  };
}

// Helper to create notifications
export function useCreateNotification() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const createNotification = useMutation({
    mutationFn: async (notification: {
      user_id: string;
      type: Notification["type"];
      title: string;
      message?: string;
      link?: string;
      entity_type?: string;
      entity_id?: string;
    }) => {
      if (!tenantId) throw new Error("Tenant não encontrado");

      const { error } = await supabase.from("notifications").insert({
        tenant_id: tenantId,
        ...notification,
      });

      if (error) throw error;
    },
  });

  return createNotification.mutate;
}
